import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import {
  AddressBookResolver,
  AddressResolver,
  AuctionBidsResolver,
  AuctionResolver,
  AuthGuard,
  BalanceResolver,
  CardTokenResolver,
  CountryResolver,
  FantasyGameResolver,
  FantasyHistoryResolver,
  LottoResolver,
  LottoWalletResolver,
  MaintenanceGuard,
  MemberCardResolver,
  MemberLoyaltyStatusResolver,
  MemberResolver,
  PartnerResolver,
  PopulationGuard,
  PortalProductsSubscriptionsResolver,
  PurchaseResolver,
  RegistrationStepResolver,
  SeatingDetailResolver,
  TransactionSuccessGuard,
  firstTimeUserGuard,
} from '@fgb/core';

// General
import { ContactUsPageComponent } from './pages/contact-us-page/contact-us-page.component';
import { FaqPageComponent } from './pages/faq-page/faq-page.component';
import { HomePageComponent } from './pages/home-page/home-page.component';
import { MasterPageComponent } from './pages/master/master-page/master-page.component';
import { NotFoundPageComponent } from './pages/not-found-page/not-found-page.component';
import { TermsPageComponent } from './pages/terms-page/terms-page.component';
// Loyalty
import { AuctionDetailPageComponent } from './pages/auction-detail-page/auction-detail-page.component';
import { AuctionsPageComponent } from './pages/auctions-page/auctions-page.component';
import { EventsPageComponent } from './pages/events-page/events-page.component';
import { LottoDetailPageComponent } from './pages/lotto-detail-page/lotto-detail-page.component';
import { LottoWalletDetailsPageComponent } from './pages/lotto-wallet-details-page/lotto-wallet-details-page.component';
import { LottosPageComponent } from './pages/lottos-page/lottos-page.component';
import { LottosSuccessPageComponent } from './pages/lottos-page/lottos-success-page/lottos-success-page.component';
import { MarketplaceDetailPageComponent } from './pages/marketplace-detail-page/marketplace-detail-page.component';
import { MarketplacePageComponent } from './pages/marketplace-page/marketplace-page.component';
import { MarketplacePurchaseSuccessPageComponent } from './pages/marketplace-page/marketplace-purchase-success-page/marketplace-purchase-success-page.component';
import { ProductsPageComponent } from './pages/products-page/products-page.component';
import { RewardsLayoutComponent } from './pages/rewards-layout/rewards-layout.component';
import { WalletPageComponent } from './pages/wallet-page/wallet-page.component';

// Account
import { AccountDetailsPageComponent } from './pages/account-details-page/account-details-page.component';
import { ActivityPageComponent } from './pages/activity-page/activity-page.component';
import { CardPageComponent } from './pages/card-page/card-page.component';
// Ecash
import { EcashHistoryPageComponent } from './pages/ecash-history/ecash-history.component';
import { EcashFailurePageComponent } from './pages/ecash-page/ecash-failure-page/ecash-failure-page.component';
import { EcashPageComponent } from './pages/ecash-page/ecash-page.component';
import { EcashResultPageComponent } from './pages/ecash-page/ecash-result-page/ecash-result-page.component';
import { EcashSuccessPageComponent } from './pages/ecash-page/ecash-success-page/ecash-success-page.component';
// Portal Products
import { PortalProductFailurePageComponent } from './pages/portal-products-pages/portal-product-failure-page/portal-product-failure-page.component';
import { PortalProductResultsPageComponent } from './pages/portal-products-pages/portal-product-results-page/portal-product-results-page.component';
import { PortalProductSuccessPageComponent } from './pages/portal-products-pages/portal-product-success-page/portal-product-success-page.component';
// Events & Tickets
import { ContactsPageComponent } from './pages/contacts-page/contacts-page.component';

// Unauthenticated
import { AuthCallbackComponent } from './modules/user/components/auth/auth-callback/auth-callback.component';
import { SingleSignOnComponent } from './modules/user/components/single-sign-on/single-sign-on.component';
import { LoginAccountsPageComponent } from './pages/login-accounts-page/login-accounts-page.component';
import { LoginPageComponent } from './pages/login-page/login-page.component';
import { MaintenancePageComponent } from './pages/maintenance-page/maintenance-page.component';
// Registration
import { RegistrationLayoutPageComponent } from './modules/registration/pages/registration-layout-page/registration-layout-page.component';
// Population Pages
import { TestPopHomePageComponent } from './pages/test-pop-home-page/test-pop-home-page.component';
// Club Pages
import { EarnPageComponent } from './pages/earn-page/earn-page.component';

// Partners Pages
import { V1SingleSignOnComponent } from './modules/user/components/v1-single-sign-on/v1-single-sign-on.component';
import { CartCheckoutPagePortalProductsComponent } from './pages/cart-checkout-page-portal-products/cart-checkout-page-portal-products.component';
import { CartCheckoutPageComponent } from './pages/cart-checkout-page/cart-checkout-page.component';
import { LottosTermsPageComponent } from './pages/lottos-terms-page/lottos-terms-page.component';
import { MarketplaceTermsPageComponent } from './pages/marketplace-terms-page/marketplace-terms-page.component';
import { RenewalBonusTransferPageComponent } from './pages/renewal-bonus-transfer-page/renewal-bonus-transfer-page.component';
import { SubscriptionsPageComponent } from './pages/subscriptions-page/subscriptions-page.component';
import { ZoomMeetingPageComponent } from './pages/zoom-meeting-page/zoom-meeting-page.component';
import { TmCallbackComponent } from './shared/components/manage-tickets/tm-callback/tm-callback.component';
import { TmAuthCallbackComponent } from './shared/components/providers/tm/tm-auth-callback/tm-auth-callback.component';
// Offer Pages
import { ScorecardDescriptionComponent } from '@fgb/portal-component-library/src/lib/loyalty/scorecards';
import { provideRegistrationConfig } from '@fgb/portal-component-library/src/lib/registration';
import { FantasyGamePlayComponent } from './modules/fantasy/fantasy-game-play/fantasy-game-play.component';
import { CampaignDetailsPageComponent } from './pages/campaign-details-page/campaign-details-page.component';
import { CardLinkActivatedOffersPageComponent } from './pages/card-link-activated-offers-page/card-link-activated-offers-page.component';
import { CardLinkAffiliatesPageComponent } from './pages/card-link-affiliates-page/card-link-affiliates-page.component';
import { CardLinkAllOffersPageComponent } from './pages/card-link-all-offers-page/card-link-all-offers-page.component';
import { CardLinkOfferDetailsPageComponent } from './pages/card-link-offer-details-page/card-link-offer-details-page.component';
import { CardLinkingPageComponent } from './pages/card-linking-page/card-linking-page.component';
import { CardLinkingSuccessPageComponent } from './pages/card-linking-success-page/card-linking-success-page.component';
import { CartCheckoutResultsPageComponent } from './pages/cart-checkout-page/cart-checkout-results-page/cart-checkout-results-page.component';
import { CartCheckoutSuccessPageComponent } from './pages/cart-checkout-page/cart-checkout-success-page/cart-checkout-success-page.component';
import { ChallengesPageComponent } from './pages/challenges-page/challenges-page.component';
import { FantasyGamesPageComponent } from './pages/fantasy-games-page/fantasy-games-page.component';
import { GamesLayoutComponent } from './pages/games-layout/games-layout.component';
import { GamesPageComponent } from './pages/games-page/games-page.component';
import { LoadedTicketsComponent } from './pages/loaded-tickets/loaded-tickets.component';
import { MarketplaceWalletPageComponent } from './pages/marketplace-wallet-page/marketplace-wallet-page.component';
import { NotificationManagementPageComponent } from './pages/notification-management-page/notification-management-page.component';
import { OfferDetailsPageComponent } from './pages/offer-details-page/offer-details-page.component';
import { OffersPageComponent } from './pages/offers-page/offers-page.component';
import { PaymentPageComponent } from './pages/payment-page/payment-page.component';
import { PollsPageComponent } from './pages/polls-page/polls-page.component';
import { PredictorLeaderboardPageComponent } from './pages/predictor-leaderboard-page/predictor-leaderboard-page.component';
import { PredictorPageComponent, PredictorPlayComponent } from './pages/predictor-page/predictor-page.component';
import { QuizLeaderboardPageComponent } from './pages/quiz-leaderboard-page/quiz-leaderboard-page.component';
import { QuizPageComponent, QuizPlayComponent } from './pages/quiz-page/quiz-page.component';
import { WalletClaimedPageComponent } from './pages/wallet-claimed-page/wallet-claimed-page.component';
import { WorksheetPageComponent } from './pages/worksheet-page/worksheet-page.component';
import { ProviderCallbackComponent } from './shared/components/providers/provider-callback/provider-callback.component';
import { SeatGeekCallbackComponent } from './shared/components/providers/seatgeek/seatgeek-callback/seatgeek-callback.component';
import { SportsAllianceCallbackComponent } from './shared/components/providers/sports-alliance/sports-alliance-callback/sports-alliance-callback.component';
import { FantasyLeaderboardComponent } from './modules/fantasy/components/fantasy-leaderboard/fantasy-leaderboard.component';
import { FantasyUpNextComponent } from './modules/fantasy/fantasy-up-next/fantasy-up-next.component';
import { FantasyGameRulesComponent } from './modules/fantasy/fantasy-game-rules/fantasy-game-rules.component';
import { FantasyGameResultsComponent } from './modules/fantasy/fantasy-game-results/fantasy-game-results.component';
import { CampaignV2DetailsOfferPageComponent } from './pages/campaign-v2-details-offer-page/campaign-v2-details-offer-page.component';
import { CampaignV2DetailsVoucherPageComponent } from './pages/campaign-v2-details-voucher-page/campaign-v2-details-voucher-page.component';

const routes: Routes = [
  {
    path: '',
    canActivate: [AuthGuard, firstTimeUserGuard()],
    canActivateChild: [MaintenanceGuard],
    component: MasterPageComponent,
    resolve: {
      member: MemberResolver,
      memberCard: MemberCardResolver,
      balances: BalanceResolver,
    },
    children: [
      {
        path: '',
        canActivate: [PopulationGuard],
        data: {
          exclude: ['testPop'],
          popRedirect: 'test',
        },
        component: HomePageComponent,
        resolve: {
          cardTokens: CardTokenResolver,
        },
      },
      {
        path: 'cards',
        component: CardPageComponent,
      },
      {
        path: 'test',
        canActivate: [PopulationGuard],
        data: {
          include: ['testPop'],
          popRedirect: '',
        },
        component: TestPopHomePageComponent,
      },
      {
        path: 'rewards',
        component: RewardsLayoutComponent,
        children: [
          {
            path: '',
            redirectTo: 'marketplace',
            pathMatch: 'full',
          },
          {
            path: 'auctions',
            children: [
              {
                path: '',
                resolve: { auctions: AuctionResolver },
                component: AuctionsPageComponent,
              },
              {
                path: 'details/:id',
                resolve: {
                  balances: BalanceResolver,
                  auctions: AuctionResolver,
                },
                component: AuctionDetailPageComponent,
              },
            ],
          },
          {
            path: 'marketplace',
            children: [
              {
                path: '',
                component: MarketplacePageComponent,
              },
              {
                path: 'details/:id',
                children: [
                  {
                    path: '',
                    resolve: {
                      balances: BalanceResolver,
                      cardTokens: CardTokenResolver,
                      country: CountryResolver,
                    },
                    component: MarketplaceDetailPageComponent,
                  },
                  {
                    path: 'terms',
                    component: MarketplaceTermsPageComponent,
                  },
                ],
              },
              {
                path: 'purchase/success',
                canActivate: [TransactionSuccessGuard],
                component: MarketplacePurchaseSuccessPageComponent,
                resolve: {
                  balances: BalanceResolver,
                  purchase: PurchaseResolver,
                },
              },
            ],
          },
          {
            path: 'events',
            component: EventsPageComponent,
          },
          {
            path: 'products',
            component: ProductsPageComponent,
          },

          {
            path: 'raffles',
            children: [
              {
                path: '',
                component: LottosPageComponent,
                resolve: { lottos: LottoResolver, balances: BalanceResolver, cardTokens: CardTokenResolver },
              },
              {
                path: 'details/:id',
                children: [
                  {
                    path: '',
                    resolve: { balances: BalanceResolver, lottos: LottoResolver },
                    component: LottoDetailPageComponent,
                  },
                  {
                    path: 'terms',
                    component: LottosTermsPageComponent,
                    resolve: { lottos: LottoResolver },
                  },
                ],
              },
              {
                path: 'purchase/success',
                canActivate: [TransactionSuccessGuard],
                component: LottosSuccessPageComponent,
              },
            ],
          },
          {
            path: 'wallet',
            children: [
              {
                path: '',
                component: WalletPageComponent,
                resolve: {
                  cardTokens: CardTokenResolver,
                },
              },
              {
                path: 'details/:id',
                component: MarketplaceWalletPageComponent,
                resolve: { purchases: PurchaseResolver },
              },
              {
                path: 'raffledetails/:id',
                component: LottoWalletDetailsPageComponent,
                resolve: { lottoWalletItems: LottoWalletResolver },
              },
            ],
          },
          {
            path: 'claimed',
            children: [
              {
                path: '',
                component: WalletClaimedPageComponent,
                resolve: {
                  purchases: PurchaseResolver,
                  lottoWalletItems: LottoWalletResolver,
                  auctionBids: AuctionBidsResolver,
                },
              },
            ],
          },
        ],
      },
      {
        path: 'account',
        component: AccountDetailsPageComponent,
        resolve: {
          member: MemberResolver,
          balances: BalanceResolver,
          seating: SeatingDetailResolver,
          memberLoyaltyStatus: MemberLoyaltyStatusResolver,
          cardTokens: CardTokenResolver,
        },
      },
      { path: 'activity', component: ActivityPageComponent },
      {
        path: 'payment',
        resolve: {
          balances: BalanceResolver,
          cardTokens: CardTokenResolver,
          memberCard: MemberCardResolver,
          countryResolver: CountryResolver,
        },
        children: [
          { path: '', component: PaymentPageComponent },
          {
            path: 'success',
            resolve: { balances: BalanceResolver },
            component: EcashSuccessPageComponent,
          },
          { path: 'failure', component: EcashFailurePageComponent },
          { path: 'result', component: EcashResultPageComponent },
        ],
      },
      {
        path: 'ecash',
        resolve: { balances: BalanceResolver, cardTokens: CardTokenResolver },
        children: [
          { path: '', component: EcashPageComponent },
          {
            path: 'success',
            resolve: { balances: BalanceResolver },
            component: EcashSuccessPageComponent,
          },
          { path: 'failure', component: EcashFailurePageComponent },
          { path: 'result', component: EcashResultPageComponent },
          {
            path: 'bonustransfer',
            resolve: {
              partners: PartnerResolver,
              balances: BalanceResolver,
            },
            component: RenewalBonusTransferPageComponent,
          },
        ],
      },
      {
        path: 'ecashhistory',
        resolve: {
          balances: BalanceResolver,
          cardTokens: CardTokenResolver,
          memberCard: MemberCardResolver,
          member: MemberResolver,
        },
        component: EcashHistoryPageComponent,
      },
      {
        path: 'portalproducts',
        resolve: { balances: BalanceResolver, cardTokens: CardTokenResolver },
        children: [
          {
            path: 'success',
            resolve: { balances: BalanceResolver },
            component: PortalProductSuccessPageComponent,
          },
          { path: 'failure', component: PortalProductFailurePageComponent },
          { path: 'result', component: PortalProductResultsPageComponent },
        ],
      },
      {
        path: 'cardtoken',
        children: [
          { path: 'success', component: EcashSuccessPageComponent },
          { path: 'failure', component: EcashFailurePageComponent },
          { path: 'result', component: EcashResultPageComponent },
        ],
      },
      {
        path: 'offers',
        children: [
          { path: '', component: OffersPageComponent },
          {
            path: 'details/:offerId',
            component: OfferDetailsPageComponent,
          },
        ],
      },
      {
        path: 'campaign',
        children: [
          {
            path: 'details/:offerId',
            component: CampaignDetailsPageComponent,
          },
        ],
      },

      {
        path: 'campaign-v2',
        children: [
          {
            path: 'details/offer/:offerId',
            component: CampaignV2DetailsOfferPageComponent,
          },
          {
            path: 'details/voucher/:voucherId',
            component: CampaignV2DetailsVoucherPageComponent,
          },
        ],
      },
      { path: 'faq', component: FaqPageComponent },
      { path: 'terms', component: TermsPageComponent },
      { path: 'contact', component: ContactUsPageComponent },
      { path: 'scorecard-description/:id', component: ScorecardDescriptionComponent },
      {
        path: 'challenges',
        component: ChallengesPageComponent,
        resolve: {
          cardTokens: CardTokenResolver,
        },
      },
      {
        path: 'loadedtickets',
        component: LoadedTicketsComponent,
        resolve: {
          cardTokens: CardTokenResolver,
        },
      },
      {
        path: 'contacts',
        component: ContactsPageComponent,
        resolve: { addressBookResolver: AddressBookResolver },
      },
      // Club Pages
      {
        path: 'checkout',
        children: [
          {
            path: '',
            component: CartCheckoutPageComponent,
            resolve: {
              member: MemberResolver,
              address: AddressResolver,
              country: CountryResolver,
            },
          },
          {
            path: 'success',
            component: CartCheckoutSuccessPageComponent,
          },
          {
            path: 'results',
            component: CartCheckoutResultsPageComponent,
          },
        ],
        resolve: {
          address: AddressResolver,
        },
      },
      {
        path: 'subscriptions',
        children: [
          {
            path: '',
            component: SubscriptionsPageComponent,
            resolve: {
              subscriptions: PortalProductsSubscriptionsResolver,
              address: AddressResolver,
            },
          },
          {
            path: 'checkout',
            component: CartCheckoutPagePortalProductsComponent,
            resolve: {
              subscriptions: PortalProductsSubscriptionsResolver,
              address: AddressResolver,
              country: CountryResolver,
              member: MemberResolver,
            },
          },
        ],
      },
      { path: 'notificationmanagement', component: NotificationManagementPageComponent },
      { path: 'earn', component: EarnPageComponent },
      { path: '404page', component: NotFoundPageComponent },
      { path: 'signin-ticketmaster', component: TmCallbackComponent },
      {
        path: 'provider/:providerid',
        component: ProviderCallbackComponent,
      },
      // Link Card
      {
        path: 'linkcard',
        children: [{ path: '', component: CardLinkingPageComponent }],
      },
      //Link Card Success
      {
        path: 'linkcardSuccess',
        children: [{ path: '', component: CardLinkingSuccessPageComponent }],
      },
      {
        path: 'affiliate',
        component: CardLinkAffiliatesPageComponent,
        children: [
          { path: 'alloffers', component: CardLinkAllOffersPageComponent },
          { path: 'myoffers', component: CardLinkActivatedOffersPageComponent },
          { path: '', pathMatch: 'full', redirectTo: 'alloffers' },
        ],
      },
      //card linking offer detail
      {
        path: 'offerdetail/:id',
        component: CardLinkOfferDetailsPageComponent,
      },

      {
        path: 'worksheet',
        component: WorksheetPageComponent,
      },
      {
        path: 'games',
        resolve: {
          games: FantasyGameResolver,
        },
        component: GamesLayoutComponent,
        children: [
          { path: '', component: GamesPageComponent },
          {
            path: 'trivia',
            component: QuizPageComponent,
            children: [
              { path: '', component: QuizPlayComponent },
              { path: 'leaderboard', component: QuizLeaderboardPageComponent },
            ],
          },
          { path: 'polls', component: PollsPageComponent },
          {
            path: 'predictor',
            component: PredictorPageComponent,
            children: [
              { path: '', component: PredictorPlayComponent },
              { path: 'leaderboard', component: PredictorLeaderboardPageComponent },
            ],
          },
          {
            path: 'pickaplayer',
            component: FantasyGamesPageComponent,
            resolve: { games: FantasyGameResolver, history: FantasyHistoryResolver },
            children: [
              {
                path: '',
                redirectTo: 'play',
                pathMatch: 'full',
              },
              { path: 'play', component: FantasyGamePlayComponent },
              { path: 'leaderboard', component: FantasyLeaderboardComponent },
              { path: 'up-next', component: FantasyUpNextComponent },
              { path: 'rules', component: FantasyGameRulesComponent },
              { path: 'results', component: FantasyGameResultsComponent },
            ],
          },
        ],
      },
    ],
  },
  {
    path: 'zoom/:purchaseId',
    component: ZoomMeetingPageComponent,
    resolve: { member: MemberResolver, purchases: PurchaseResolver },
  },
  { path: '', redirectTo: 'login', pathMatch: 'full' },
  { path: 'login', component: LoginPageComponent, canActivate: [MaintenanceGuard] },
  { path: 'login/accounts', component: LoginAccountsPageComponent, canActivate: [MaintenanceGuard] },
  {
    path: 'register',
    canActivateChild: [MaintenanceGuard],
    resolve: { registrationStep: RegistrationStepResolver, country: CountryResolver },
    children: [],
  },
  {
    path: 'register',
    component: RegistrationLayoutPageComponent,
    loadChildren: () => import('@fgb/portal-component-library/src/lib/registration').then((m) => m.RegistrationModule),
    providers: [provideRegistrationConfig()],
  },
  { path: 'callback', component: AuthCallbackComponent },
  { path: 'sso', component: SingleSignOnComponent },
  { path: 'login/singleSignOn/:providerId', component: SingleSignOnComponent },
  { path: 'login/ss/login.aspx', component: V1SingleSignOnComponent },
  { path: 'maintenance', component: MaintenancePageComponent },
  { path: 'tm-auth-callback', component: TmAuthCallbackComponent },
  { path: 'sa-callback', component: SportsAllianceCallbackComponent },
  { path: 'sg-callback', component: SeatGeekCallbackComponent },
  { path: '**', redirectTo: '404page' },
];

@NgModule({
  imports: [RouterModule.forRoot(routes, { scrollPositionRestoration: 'enabled', bindToComponentInputs: true })],
  exports: [RouterModule],
})
export class AppRoutingModule {}
